import { useState, useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useBoundValue } from "../hooks/use-bound-value"
import { cn } from "../lib/utils"
import { STAR_SIZE, STAR_COLOR } from "../lib/style-resolver"

export function StarRatingRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext()
  const authoredMax = element.max ?? (element.options as { maxStars?: unknown } | undefined)?.maxStars
  const maxStars = Math.min(20, Math.max(1, Number(authoredMax) || 5))
  const initialValue = (element.options as any)?.value ?? 0

  const [rating, setRating] = useBoundValue<number>(
    element.bind,
    resolveSymbol,
    initialValue,
    Number,
  )
  const [hover, setHover] = useState<number>(0)

  const starSize = STAR_SIZE[element.size ?? "default"] ?? STAR_SIZE.default

  const handleClick = useCallback(
    (value: number) => {
      setRating(value)
      // forward per-item itemContext (parity with button/checkbox) so a block-ref'd row keeps item identity
      dispatchEvent(element.id, "change", { ...(itemContext ?? {}), value })
    },
    [element.id, dispatchEvent, setRating, itemContext],
  )

  return (
    <div
      className={cn("flex flex-row gap-1 items-center", element.className)}
      data-testid={`star-rating-${element.id}`}
      data-value={rating}
      data-max={maxStars}
      role="radiogroup"
      aria-label={element.ariaLabel ?? "Star rating"}
    >
      {Array.from({ length: maxStars }, (_, i) => {
        const value = i + 1
        const filled = value <= (hover || rating)
        return (
          <button
            key={i}
            type="button"
            className="bg-transparent border-none cursor-pointer p-0 transition-transform hover:scale-110"
            onMouseEnter={() => setHover(value)}
            onMouseLeave={() => setHover(0)}
            onClick={() => handleClick(value)}
            role="radio"
            aria-checked={value === rating}
            aria-posinset={value}
            aria-setsize={maxStars}
            aria-label={`Rate ${value} star${value > 1 ? "s" : ""}`}
          >
            <svg
              width={starSize}
              height={starSize}
              viewBox="0 0 24 24"
              fill={filled ? STAR_COLOR : "none"}
              stroke={filled ? STAR_COLOR : "currentColor"}
              strokeWidth="2"
              className={filled ? "text-yellow-400" : "text-muted-foreground"}
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
        )
      })}
    </div>
  )
}
